import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, DollarSign, Package, ChevronRight, Briefcase, Target, Check, AlertCircle, Calendar, Loader2, Star } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { sessionsAPI, jobsAPI } from '../services/api';
import NotificationBell from '../components/NotificationBell';

const HomePage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [error, setError] = useState(null);

  // Default values when not logged in or no data
  const defaultStats = {
    totalDeliveries: 0,
    totalEarnings: 0,
    totalJobs: 0,
    weeklyDeliveries: 0,
    weeklyEarnings: 0,
    daysWorked: 0,
    weeklyActivity: [
      { day: 'Mon', deliveries: 0, earnings: 0, hours: 0, worked: false },
      { day: 'Tue', deliveries: 0, earnings: 0, hours: 0, worked: false },
      { day: 'Wed', deliveries: 0, earnings: 0, hours: 0, worked: false },
      { day: 'Thu', deliveries: 0, earnings: 0, hours: 0, worked: false },
      { day: 'Fri', deliveries: 0, earnings: 0, hours: 0, worked: false },
      { day: 'Sat', deliveries: 0, earnings: 0, hours: 0, worked: false },
      { day: 'Sun', deliveries: 0, earnings: 0, hours: 0, worked: false },
    ],
  };

  // Day translations
  const dayTranslations = {
    'Mon': t('day_mon'),
    'Tue': t('day_tue'),
    'Wed': t('day_wed'),
    'Thu': t('day_thu'),
    'Fri': t('day_fri'),
    'Sat': t('day_sat'),
    'Sun': t('day_sun'),
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch user stats from sessions
        const userStats = await sessionsAPI.getMyStats();
        setStats(userStats);

        // Fetch active job
        try {
          const activeJobResponse = await jobsAPI.getMyActiveJob();
          setActiveJob(activeJobResponse.data);
        } catch (err) {
          // No active job is fine
          setActiveJob(null);
        }
        
      } catch (err) {
        console.error('Error fetching home data:', err);
        setError(err.message);
        setStats(defaultStats);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Use stats or defaults
  const displayStats = stats || defaultStats;
  const weeklyData = displayStats.weeklyActivity || defaultStats.weeklyActivity;
  
  // Calculate delivery goal progress
  const dailyGoal = 500; // Default daily goal
  const deliveryProgress = Math.min((displayStats.weeklyDeliveries / dailyGoal) * 100, 100);

  // Format due date and check if it's soon
  const formatDueDate = (dateString) => {
    if (!dateString) return { formatted: '', diffDays: 999, isUrgent: false };
    const dueDate = new Date(dateString);
    const today = new Date();
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    const formatted = dueDate.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
    
    return { formatted, diffDays, isUrgent: diffDays <= 2 };
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-2" />
          <p className="text-gray-500">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="home-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 text-white px-5 pt-12 pb-24 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-orange-100 text-sm font-medium">{t('home_greeting')}</p>
              <h1 className="text-2xl font-bold mt-1" data-testid="user-greeting">
                {user?.name?.split(' ')[0] || 'Guest'} 👋
              </h1>
              {/* Star Rating Display */}
              {user?.star_rating !== undefined && user?.star_rating !== null && (
                <div className="flex items-center gap-1 mt-2" data-testid="user-star-rating">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      size={14}
                      className={star <= Math.round(user.star_rating) ? 'text-yellow-300 fill-yellow-300' : 'text-white/30'}
                    />
                  ))}
                  <span className="text-sm text-orange-100 ml-1">({user.star_rating.toFixed(1)})</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="text-white">
                <NotificationBell />
              </div>
              <div 
                className="w-12 h-12 rounded-full border-2 border-white/30 bg-orange-400 flex items-center justify-center text-white font-bold text-lg cursor-pointer"
                onClick={() => navigate('/profile')}
              >
                {user?.name?.charAt(0) || 'G'}
              </div>
            </div>
          </div>

          {/* Today's Delivery Goal Card */}
          <Card className="bg-white/15 backdrop-blur-sm border-0 p-4 text-white">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target size={18} className="text-yellow-300" />
                <span className="font-semibold">{t('home_todays_goal')}</span>
              </div>
              <span className="text-sm text-orange-100">{displayStats.weeklyDeliveries} / {dailyGoal}</span>
            </div>
            <Progress value={deliveryProgress} className="h-2 bg-white/20" />
            <p className="text-xs text-orange-100 mt-2">
              {Math.round(deliveryProgress)}% {t('home_complete')} • {Math.max(0, dailyGoal - displayStats.weeklyDeliveries)} {t('home_deliveries_to_go')}
            </p>
          </Card>
        </div>
      </div>

      {/* Quick Stats Cards */}
      <div className="px-5 -mt-12 relative z-20">
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-white p-4 shadow-lg shadow-gray-200/50 border-0 text-center" data-testid="deliveries-stat">
            <div className="w-10 h-10 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-2">
              <Package size={20} className="text-orange-600" />
            </div>
            <p className="text-xl font-bold text-gray-900">{displayStats.weeklyDeliveries}</p>
            <p className="text-xs text-gray-500 mt-1">{t('track_deliveries')}</p>
          </Card>
          <Card className="bg-white p-4 shadow-lg shadow-gray-200/50 border-0 text-center" data-testid="earnings-stat">
            <div className="w-10 h-10 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-2">
              <DollarSign size={20} className="text-green-600" />
            </div>
            <p className="text-xl font-bold text-gray-900">${displayStats.weeklyEarnings.toFixed(2)}</p>
            <p className="text-xs text-gray-500 mt-1">{t('track_earned')}</p>
          </Card>
          <Card className="bg-white p-4 shadow-lg shadow-gray-200/50 border-0 text-center" data-testid="jobs-stat">
            <div className="w-10 h-10 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-2">
              <Briefcase size={20} className="text-blue-600" />
            </div>
            <p className="text-xl font-bold text-gray-900">{displayStats.totalJobs}</p>
            <p className="text-xs text-gray-500 mt-1">{t('profile_jobs')}</p>
          </Card>
        </div>
      </div>

      {/* Start Delivering Button */}
      <div className="px-5 mt-6">
        <button
          onClick={() => navigate('/track')}
          data-testid="start-delivering-home-btn"
          className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-4 rounded-2xl font-semibold text-lg shadow-lg shadow-orange-500/25 flex items-center justify-center gap-3 transform transition-all duration-200 hover:shadow-xl hover:shadow-orange-500/30 active:scale-[0.98]"
        >
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Play size={22} fill="white" />
          </div>
          {t('home_start_delivering')}
        </button>
      </div>

      {/* Weekly Activity - Days Worked */}
      <div className="px-5 mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">{t('home_weekly_activity')}</h2>
          <span className="text-sm text-gray-500">
            {displayStats.daysWorked} {t('home_days_worked')}
          </span>
        </div>
        <Card className="bg-white p-4 border-0 shadow-sm" data-testid="weekly-activity-card">
          <div className="flex items-center justify-between gap-2">
            {weeklyData.map((day, index) => {
              const isToday = index === weeklyData.length - 1;
              return (
                <div key={day.day} className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                      day.worked
                        ? 'bg-gradient-to-br from-orange-500 to-red-500'
                        : 'bg-gray-100'
                    } ${isToday ? 'ring-2 ring-orange-300 ring-offset-2' : ''}`}
                  >
                    {day.worked ? (
                      <Check size={20} className="text-white" />
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                  </div>
                  <span className={`text-xs mt-2 font-medium ${
                    isToday ? 'text-orange-600' : day.worked ? 'text-gray-700' : 'text-gray-400'
                  }`}>
                    {dayTranslations[day.day] || day.day}
                  </span>
                  {day.worked && (
                    <span className="text-[10px] text-gray-400 mt-0.5">
                      {day.hours}h
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-4 border-t flex items-center justify-between">
            <div className="text-center flex-1">
              <p className="text-lg font-bold text-gray-900">{weeklyData.reduce((sum, d) => sum + d.deliveries, 0)}</p>
              <p className="text-xs text-gray-500">{t('home_total_deliveries')}</p>
            </div>
            <div className="w-px h-10 bg-gray-200" />
            <div className="text-center flex-1">
              <p className="text-lg font-bold text-green-600">${weeklyData.reduce((sum, d) => sum + d.earnings, 0).toFixed(2)}</p>
              <p className="text-xs text-gray-500">{t('home_total_earned')}</p>
            </div>
            <div className="w-px h-10 bg-gray-200" />
            <div className="text-center flex-1">
              <p className="text-lg font-bold text-gray-900">{weeklyData.reduce((sum, d) => sum + d.hours, 0).toFixed(1)}h</p>
              <p className="text-xs text-gray-500">{t('track_duration')}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Active Job - Only 1 allowed at a time */}
      <div className="px-5 mt-8 pb-24">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">{t('home_active_job')}</h2>
          <button onClick={() => navigate('/jobs')} className="text-orange-600 text-sm font-medium flex items-center gap-1" data-testid="find-jobs-link">
            {t('jobs_find')} <ChevronRight size={16} />
          </button>
        </div>
        <div className="space-y-3" data-testid="active-job-container">
          {activeJob ? (
            (() => {
              const progress = activeJob.deliveries_count ? (activeJob.deliveries_count / 100) * 100 : 0;
              const dueDate = formatDueDate(activeJob.deadline);
              return (
                <Card key={activeJob.id} className="bg-white p-4 border-0 shadow-sm" data-testid="active-job-card">
                  {/* Due Date Alert */}
                  {activeJob.deadline && (
                    <div className={`flex items-center gap-2 mb-3 px-3 py-2 rounded-lg ${
                      dueDate.isUrgent 
                        ? 'bg-red-50 border border-red-200' 
                        : 'bg-orange-50 border border-orange-200'
                    }`}>
                      <AlertCircle size={16} className={dueDate.isUrgent ? 'text-red-500' : 'text-orange-500'} />
                      <div className="flex items-center gap-2 flex-1">
                        <span className={`text-xs font-bold ${dueDate.isUrgent ? 'text-red-600' : 'text-orange-600'}`}>
                          {t('home_due_date')}:
                        </span>
                        <Calendar size={14} className={dueDate.isUrgent ? 'text-red-500' : 'text-orange-500'} />
                        <span className={`text-xs font-medium ${dueDate.isUrgent ? 'text-red-700' : 'text-orange-700'}`}>
                          {dueDate.formatted}
                        </span>
                      </div>
                      {dueDate.isUrgent && (
                        <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full font-bold">
                          {dueDate.diffDays <= 0 ? t('home_overdue') : `${dueDate.diffDays} ${t('home_days_left')}`}
                        </span>
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Briefcase size={24} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{activeJob.job_title}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{activeJob.client}</p>
                      <div className="mt-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-500">{Math.round(progress)}% {t('home_complete')}</span>
                          <span className="text-xs font-medium text-orange-600">{activeJob.deliveries_count} {t('track_deliveries')}</span>
                        </div>
                        <Progress value={progress} className="h-1.5" />
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-green-600 font-medium">${activeJob.earnings?.toFixed(2) || '0.00'} {t('track_earned').toLowerCase()}</span>
                        <span className="text-xs text-gray-400">{activeJob.status}</span>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })()
          ) : (
            <Card className="bg-white p-8 border-0 shadow-sm text-center" data-testid="no-active-job-card">
              <Briefcase size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 mb-3">{t('home_no_active_job')}</p>
              <button 
                onClick={() => navigate('/jobs')}
                className="text-orange-600 font-medium text-sm"
                data-testid="browse-jobs-btn"
              >
                {t('home_browse_jobs')} →
              </button>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePage;
